import { NavHeader } from '@/components/features/shared/NavHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { InvoiceStatus, VoucherType } from '@/domain/invoices/invoice'
import { useDataInvoice, useUpdateInvoiceItems } from '@/hooks/api/useInvoices'
import { useToast } from '@/hooks/use-toast'
import { Paths } from '@/routes/paths'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, Check, Loader2, Pencil, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { PageContainer } from '@/components/features/shared/PageContainer'

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(amount)
}

export function EditInvoiceItems() {
  const routerParams = useParams({ strict: false })
  const invoiceId = (routerParams as { invoiceId?: string }).invoiceId || ''
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: invoice, isLoading } = useDataInvoice(invoiceId)
  const updateItemsMutation = useUpdateInvoiceItems()

  type FormValues = { items: { id: string; description: string }[] }

  const form = useForm<FormValues>({
    defaultValues: {
      items: [],
    },
  })

  const { fields, update } = useFieldArray<FormValues>({
    control: form.control,
    name: 'items',
  })

  useEffect(() => {
    if (invoice?.items) {
      form.reset({
        items: invoice.items.map(item => ({ id: item.id, description: item.description })),
      })
    }
  }, [invoice, form])

  const [editingItems, setEditingItems] = useState<Record<string, boolean>>({})

  const handleEdit = (id: string) => {
    setEditingItems(prev => ({ ...prev, [id]: true }))
  }

  const handleSaveEdit = (id: string) => {
    setEditingItems(prev => ({ ...prev, [id]: false }))
  }

  const handleCancelEdit = (index: number, id: string) => {
    const original = invoice?.items?.find(i => i.id === id)
    if (original) {
      form.setValue(`items.${index}.description` as const, original.description)
    }
    setEditingItems(prev => ({ ...prev, [id]: false }))
  }

  const handleSubmitForm = async () => {
    const formItems = form.getValues('items')
    if (formItems.length === 0 || !invoice?.items) return

    const itemsToUpdate = formItems
      .filter((field, index) => {
        const state = form.getFieldState(`items.${index}.description` as const)
        return state.isDirty
      })
      .map(field => ({
        itemId: field.id,
        description: field.description.trim(),
      }))

    if (itemsToUpdate.length === 0) {
      toast({
        title: 'Sin cambios',
        description: 'No se realizaron modificaciones en las descripciones.',
        variant: 'success',
      })
      navigate({ to: `${Paths.Management.Invoicing}/generacion/${invoiceId}` })
      return
    }

    try {
      await updateItemsMutation.mutateAsync({ id: invoiceId, items: itemsToUpdate })
      queryClient.invalidateQueries({ queryKey: ['invoices', 'data-list', { id: invoiceId }] })
      navigate({ to: `${Paths.Management.InvoiceGenerationStatus}/${invoiceId}`, search: { voucherType: invoice.voucherType } })
    } catch {
      toast({
        title: 'Error',
        description: 'Error al actualizar las descripciones.',
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <Card className="w-full max-w-3xl mx-auto">
        <CardContent className="pt-12 flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <p className="text-muted-foreground">Cargando factura...</p>
        </CardContent>
      </Card>
    )
  }

  if (!invoice) {
    return (
      <Card className="w-full max-w-lg mx-auto">
        <CardContent className="pt-12 text-center">
          <p className="text-muted-foreground">Factura no encontrada</p>
          <div className="mt-4">
            <Button variant="secondary" onClick={() => navigate({ to: `${Paths.Management.Invoicing}/generacion/${invoiceId}` })}>
              Volver
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  const canEdit = invoice?.voucherType === VoucherType.RECEIPT ||
    (invoice?.voucherType === VoucherType.INVOICE && invoice?.status !== InvoiceStatus.AUTHORIZED)

  return (
    <PageContainer>
      <NavHeader>
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">Editar factura</h1>
          <p className="text-primary text-muted-foreground">
            Modifica las descripciones de los items de la factura. Solo se pueden editar las descripciones.
          </p>
        </div>
      </NavHeader>
      <form onSubmit={form.handleSubmit(handleSubmitForm)}>
        <Card className="w-full max-w-5xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Detalle de la factura</span>
              <span className="text-sm font-normal text-muted-foreground">
                N{invoice.invoiceNumber} - {invoice.customerName}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-6 pt-4 border-t">
              <h3 className="text-md font-medium mb-4">Items de la factura</h3>
              <div className="space-y-4">
                {fields.map((field, index) => {
                  const originalItem = invoice.items?.find(i => i.id === field.id)
                  const description = form.watch(`items.${index}.description` as const)
                  const isEditing = editingItems[field.id]

                  return (
                    <div key={field.id} className="p-4 border rounded-lg">
                      <div className="flex items-end md:items-start gap-4 mb-3">
                        <div className="flex-[2]">
                          <div className="flex flex-col md:flex-row items-center gap-2 mb-2">
                            <Label className="text-muted-foreground text-sm">Descripción:</Label>
                            {isEditing ? (
                              <Textarea
                                {...form.register(`items.${index}.description` as const)}
                                className="flex-1"
                                autoFocus
                              />
                            ) : (
                              <div className="flex-1 flex items-center justify-between">
                                <span className="text-sm">{description}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(field.id)}
                                  className="p-1 h-auto"
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        {isEditing && (
                          <div className="flex flex-col md:flex-row gap-2">
                            <Button type="button" size="sm" className="flex items-center gap-1 h-8"
                              onClick={() => handleSaveEdit(field.id)}>
                              <Check className="w-3 h-3" />
                              <span className='hidden md:inline'>Guardar</span>
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancelEdit(index, field.id)}
                              className="flex items-center h-8"
                            >
                              <X className="w-3 h-3" />
                              <span className='hidden md:inline'>Cancelar</span>
                            </Button>
                          </div>
                        )}
                      </div>
                      {originalItem && (
                        <div className="grid grid-cols-6 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Cantidad: </span>
                            <span className="font-medium">{originalItem.quantity}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Precio unitario: </span>
                            <span className="font-medium">{formatCurrency(originalItem.unitPrice)}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">IVA: </span>
                            <span className="font-medium">{originalItem.taxRate}%</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Impuesto: </span>
                            <span className="font-medium">{formatCurrency(originalItem.taxAmount!)}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Subtotal: </span>
                            <span className="font-medium">{formatCurrency(originalItem.subtotal)}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Total: </span>
                            <span className="font-medium">{formatCurrency(originalItem.totalAmount)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <Button type="button" variant="outline"
                onClick={() => navigate({ to: `${Paths.Management.Invoicing}/generacion/${invoiceId}` })}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!canEdit || updateItemsMutation.isPending}>
                {updateItemsMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Guardar cambios
              </Button>
            </div>

            {!canEdit && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm">
                  No se pueden editar items de facturas autorizadas.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </form>
    </PageContainer>
  )
}
